import { AnyObject, Callable, Maybe, Optional } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import {
  IFirebaseDependencies,
  TCollectionReference,
  TFirebaseApp,
  TFirebaseDatabase,
  TFirebaseEventType,
  TFirebaseFirestoreModule,
  TFirebaseOnDisconnect,
  TFirebasePrescriber,
  TFirebaseQuery,
  TFirebaseReference,
  TFirebaseSnapshot,
  TFirebaseTransactionOptions,
  TFirebaseTransactionResult,
  TFirestore,
  TFirestoreQuery,
  TFirestoreQuerySnapshot,
  TFirestoreUnsubscribe,
  TQueryFieldFilterConstraint,
  TWhereFilterOperator,
} from '../../types/firebase';
import { LiveError } from '../error';
import { ELiveErrorCategory, trackLiveAction, trackLiveError } from '../tracking';
import { firebaseListenersRegistry, firebaseLog } from './logging';

// todo: firestore_cruds + realtime_database_cruds

/**
 * Get firebase realtime database instance for an application.
 */
export function getRealtimeDatabase(
  firebase: IFirebaseDependencies,
  application: TFirebaseApp,
): TFirebaseDatabase {
  return firebase.database.getDatabase(application);
}

/**
 * Get root reference for provided database.
 */
export function getDatabaseRootRef(
  firebase: IFirebaseDependencies,
  database: TFirebaseDatabase,
): TFirebaseReference {
  return firebase.database.ref(database).root;
}

/**
 * Get reference for provided database.
 */
export function getDatabaseRef(
  firebase: IFirebaseDependencies,
  database: TFirebaseDatabase,
  path: string,
): TFirebaseReference {
  return firebase.database.ref(database, path);
}

/**
 * Subscribe to ref value.
 */
export function onRefEvent<T = unknown>(
  firebase: IFirebaseDependencies,
  ref: TFirebaseQuery,
  event: TFirebaseEventType,
  callback: (snapshot: TFirebaseSnapshot<T>, previousKey?: Maybe<string>) => void,
  comment: string = '',
): Callable {
  const key: string = `${event}#${ref.toString()}`;

  firebaseListenersRegistry.has(key)
    ? firebaseListenersRegistry.set(key, (firebaseListenersRegistry.get(key) as number) + 1)
    : firebaseListenersRegistry.set(key, 1);

  firebaseLog.info('Subscribing to:', comment, event, '🔗', ref.toString());

  const subscriptionCallback = (error: Error) => {
    // Since this cancel is not expected, also do noisy log in global context.
    Logger.getGlobal().error('Subscription cancel:', comment, error, '🔗', ref.toString());

    firebaseLog.error('Subscription cancel:', comment, error, '🔗', ref.toString());

    // Check if error is related to subscription canceling with permission deny / cancelling.
    // In this case we just track it for count and do not fire exceptions for alerts.
    const isPermissionDeniedError: boolean = Boolean(
      error?.message?.startsWith('permission_denied at /'),
    );

    if (isPermissionDeniedError) {
      trackLiveAction('fb_permission_denied', {
        message: error.message,
        key,
        event,
        comment,
      });
    } else {
      trackLiveError(error, {
        category: ELiveErrorCategory.FIREBASE,
        data: { key, event, comment },
      });
    }
  };

  switch (event) {
    case 'child_added':
      return firebase.database.onChildAdded(ref, callback, subscriptionCallback);
    case 'child_changed':
      return firebase.database.onChildChanged(ref, callback, subscriptionCallback);
    case 'child_moved':
      return firebase.database.onChildMoved(ref, callback, subscriptionCallback);
    case 'child_removed':
      return firebase.database.onChildRemoved(ref, callback, subscriptionCallback);
    case 'value':
      return firebase.database.onValue(ref, callback, subscriptionCallback);
  }
}

/**
 * Unsubscribe to ref event and track flow.
 */
export function offRefEvent<T = unknown>(
  firebase: IFirebaseDependencies,
  ref: TFirebaseReference,
  event: TFirebaseEventType,
  callback: (snapshot: TFirebaseSnapshot<T>, previousKey?: Maybe<string>) => void,
): void {
  const key: string = `${event}#${ref.toString()}`;
  const subscribersCount: number = (firebaseListenersRegistry.get(key) as number) || 0;

  if (subscribersCount > 1) {
    firebaseListenersRegistry.set(key, subscribersCount - 1);
  } else {
    firebaseListenersRegistry.delete(key);
  }

  firebaseLog.info('Un-subscribing from:', event, '🔗', ref.toString());

  firebase.database.off(ref, event, callback);
}

/**
 * Get firebase disconnect handler for ref.
 */
export function onRefDisconnect(
  firebase: IFirebaseDependencies,
  ref: TFirebaseReference,
): TFirebaseOnDisconnect {
  return firebase.database.onDisconnect(ref);
}

/**
 * Collect single batch updates object based on root and provided prescribers.
 * Just utility method for batched DB update.
 */
function collectBatchChanges(
  rootRef: TFirebaseReference,
  records: Array<Optional<TFirebasePrescriber>>,
): Record<string, unknown> {
  const rootPath: string = rootRef.toString();

  return records.reduce((acc, prescriber) => {
    if (prescriber) {
      const [ref, value] = prescriber;
      const updatePath: string = decodeURIComponent(ref.toString()).substring(rootPath.length);

      if (updatePath in acc) {
        throw new LiveError(`Duplicated ref update supplied for batch updater: '${updatePath}'.`);
      }

      acc[updatePath] = value;
    }

    return acc;
  }, {});
}

/**
 * Do batched update of few fields to do only one operation instead of few.
 */
export function batchedDatabaseUpdate(
  firebase: IFirebaseDependencies,
  rootRef: TFirebaseReference,
  records: Array<Optional<TFirebasePrescriber>>,
): Promise<void> {
  const updates: Record<string, unknown> = collectBatchChanges(rootRef, records);

  firebaseLog.info('Batched update:', updates);

  /**
   * Collect whole update object, fill it in and perform update with a single action.
   * Get ref path relative to root instead of full URL and pack whole object for sending.
   *
   * Make sure that any updates are needed before calling update method.
   */
  if (Object.keys(updates).length) {
    return firebase.database.update(rootRef, updates);
  } else {
    return Promise.resolve();
  }
}

/**
 * Based on ref and required fields collect one big prescriber.
 * Used to filter out difference between front-end side model and server representation.
 *
 * Example use case:
 *  I want to update an object with 20 fields, but pick only selected 4-5 items.
 */
export function collectObjectBatchPrescribers<T extends AnyObject>({
  firebase,
  ref,
  object,
  keys,
}: {
  firebase: IFirebaseDependencies;
  ref: TFirebaseReference;
  object: T;
  keys: Array<keyof T>;
}): Array<TFirebasePrescriber> {
  return keys.map((it) => [firebase.database.child(ref, String(it)), object[it]]);
}

/**
 * Set ref value in firebase with middleware logging.
 */
export function setRefValue<T = unknown>(
  firebase: IFirebaseDependencies,
  ref: TFirebaseReference,
  value: T,
): Promise<void> {
  firebaseLog.info('Set ref value:', ref.toString(), value);
  return firebase.database.set(ref, value);
}

/**
 * Run transaction to DB that is based on previous value and locks ref mutations to prevent corrupted data state.
 */
export function runRefTransaction<T = unknown>(
  firebase: IFirebaseDependencies,
  ref: TFirebaseReference,
  updater: (value: T) => unknown,
  options?: TFirebaseTransactionOptions,
): Promise<TFirebaseTransactionResult> {
  firebaseLog.info('Run transaction:', ref.toString(), options);
  return firebase.database.runTransaction(ref, updater, options);
}

/**
 * Set ref value in firebase with middleware logging, based on prescriber.
 */
export async function setRefPrescriberValue<T = unknown>(
  firebase: IFirebaseDependencies,
  prescriber: Optional<TFirebasePrescriber<T>>,
): Promise<void> {
  if (prescriber) {
    await setRefValue(firebase, prescriber[0], prescriber[1]);
  } else {
    firebaseLog.info('Set ref value skip, null received');
  }
}

/**
 * Update ref value in firebase with middleware logging, based on prescriber.
 */
export async function updateRefPrescriberValue<T extends AnyObject = AnyObject>(
  firebase: IFirebaseDependencies,
  prescriber: Optional<TFirebasePrescriber<T>>,
): Promise<void> {
  if (prescriber) {
    await updateRefValue(firebase, prescriber[0], prescriber[1]);
  } else {
    firebaseLog.info('Update ref value skip, null received');
  }
}

/**
 * Update ref value in firebase with middleware logging.
 */
export function updateRefValue<T extends AnyObject = AnyObject>(
  firebase: IFirebaseDependencies,
  ref: TFirebaseReference,
  value: T,
): Promise<void> {
  firebaseLog.info('Update ref value:', ref.toString(), value);
  return firebase.database.update(ref, value);
}

/**
 * Remove ref value in firebase with middleware logging.
 */
export function removeRefValue(
  firebase: IFirebaseDependencies,
  ref: TFirebaseReference,
): Promise<void> {
  firebaseLog.info('Remove ref value:', ref.toString());

  return firebase.database.remove(ref);
}

/**
 * Remove ref value in firebase with middleware logging, based on prescriber.
 */
export async function removeRefPrescriberValue<T = unknown>(
  firebase: IFirebaseDependencies,
  prescriber: Optional<TFirebasePrescriber<T>>,
): Promise<void> {
  if (prescriber) {
    await removeRefValue(firebase, prescriber[0]);
  } else {
    firebaseLog.info('Remove ref value skip, null received');
  }
}

/**
 * Push ref value in firebase with middleware logging.
 */
export function pushRefValue<T = unknown>(
  firebase: IFirebaseDependencies,
  ref: TFirebaseReference,
  value?: T,
): Promise<TFirebaseReference> {
  firebaseLog.info('Push ref value:', ref.toString());

  return firebase.database.push(ref, value) as unknown as Promise<TFirebaseReference>;
}

/**
 * Get ref value in firebase with middleware logging.
 */
export async function getRefValue<T = unknown>(
  firebase: IFirebaseDependencies,
  ref: TFirebaseQuery,
): Promise<T> {
  firebaseLog.info('Get ref value:', ref.toString());

  return (
    await firebase.database.get(ref).catch((error) => {
      firebaseLog.error('Get ref value error:', error, ref.toString());
      throw error;
    })
  ).val();
}

/**
 * Get ref child.
 */
export function getRefChild(
  firebase: IFirebaseDependencies,
  ref: TFirebaseReference,
  child: string,
): TFirebaseReference {
  return firebase.database.child(ref, child);
}

export function getFirestore(firestore: TFirebaseFirestoreModule, app: TFirebaseApp): TFirestore {
  return firestore.getFirestore(app);
}

export function getFirestoreCollectionRef(
  firestore: TFirebaseFirestoreModule,
  firestoreObj: TFirestore,
  path: string,
): TCollectionReference {
  return firestore.collection(firestoreObj, path);
}

export function getFirestoreFilterConstraint(
  firestore: TFirebaseFirestoreModule,
  field: string,
  whereFilterOperator: TWhereFilterOperator,
  value: unknown,
): TQueryFieldFilterConstraint {
  return firestore.where(field, whereFilterOperator, value);
}

export function getFirestoreFilteredQuery(
  firestore: TFirebaseFirestoreModule,
  collectionReference: TCollectionReference,
  filterConstraint: TQueryFieldFilterConstraint,
): TFirestoreQuery {
  return firestore.query(collectionReference, filterConstraint);
}

export function onFirestoreQuerySnapshot(
  firestore: TFirebaseFirestoreModule,
  ref: TFirestoreQuery,
  callback: (snapshot: TFirestoreQuerySnapshot) => void,
): TFirestoreUnsubscribe {
  return firestore.onSnapshot(ref, callback);
}
